import React, { useEffect, useState } from "react";
import { MapPin, Heart, Leaf, ArrowRight, Phone, Mail, Globe, BookOpen, Coffee, Home, Sparkles, Target, Baby, Flame, Users, Brain, Flower2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import NavBar from '@/components/NavBar';
import { ReturnToTop } from '@/components/ReturnToTop';
import ScrollDownArrow from '@/components/ScrollDownArrow';
import { BOOKING_URL } from '@/constants/urls';
import treatmentImage from '@/assets/treatment.jpg';

const MiscarriageSupportPage = () => {
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    document.title = "Miscarriage & Early Pregnancy Loss Support Sunshine Coast | Chinese Medicine";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Honouring your loss. Supporting your healing. Holding space for what comes next. Miscarriage and early pregnancy loss support with Chinese medicine in Currimundi.");
    }
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', "Miscarriage & Early Pregnancy Loss Support Sunshine Coast | Chinese Medicine");
    }
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', "Honouring your loss. Supporting your healing. Holding space for what comes next. Miscarriage and early pregnancy loss support with Chinese medicine in Currimundi.");
    }
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', "https://womenandtheway.com.au/miscarriage-support");
    }
    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) {
      ogImage.setAttribute('content', "https://womenandtheway.com.au/treatment.jpg");
    }
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', "Miscarriage & Early Pregnancy Loss Support Sunshine Coast | Chinese Medicine");
    }
    const schema = {
      "@context": "https://schema.org",
      "@type": "HealthAndBeautyBusiness",
      "name": "Women and The Way - Miscarriage & Early Pregnancy Loss Support",
      "image": "https://womenandtheway.com.au/treatment.jpg",
      "description": "Honouring your loss. Supporting your healing. Holding space for what comes next. Miscarriage and early pregnancy loss support with Chinese medicine in Currimundi.",
      "url": "https://womenandtheway.com.au/miscarriage-support",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "700 Nicklin Way",
        "addressLocality": "Currimundi",
        "addressRegion": "QLD",
        "addressCountry": "AU"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": -26.5270,
        "longitude": 153.0890
      },
      "priceRange": "$$",
      "serviceType": "Miscarriage Support, Pregnancy Loss Support, Grief Support, Chinese Medicine",
      "sameAs": [
        "https://www.facebook.com/Womenandtheway",
        "https://www.instagram.com/women_and_the_way"
      ]
    };
    const existingSchema = document.querySelector('script[type="application/ld+json"]');
    if (existingSchema) existingSchema.remove();
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => { if (script.parentNode) script.parentNode.removeChild(script); };
  }, []);

  const handleBooking = async () => {
    setIsLoading(true);
    try {
      window.open(BOOKING_URL, '_blank');
    } catch (error) {
      console.error('Booking error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-b from-white via-white to-amber-50">
        <NavBar />
        {/* Hero Section */}
        <section className="relative py-20 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
                  Miscarriage & Early Pregnancy Loss Support
                </h1>
                <p className="text-xl md:text-2xl text-sage-600 mb-6 font-medium">
                  Honouring your loss. Supporting your healing. Holding space for what comes next.
                </p>
                <p className="text-lg text-sage-700 mb-8 leading-relaxed">
                  Miscarriage and early pregnancy loss are more common than most people realise - yet so often, they're met with silence. At Women and the Way, we believe this moment deserves compassion, acknowledgement, and meaningful care. Whether your loss was recent, a long time ago, or something you're still carrying, we offer gentle Chinese medicine treatments to help you process, heal, and feel held - physically, emotionally, and spiritually.
                </p>
                <Button 
                  size="lg" 
                  className="bg-sage-600 hover:bg-sage-700 text-white font-medium text-base px-8 py-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2"
                  onClick={handleBooking}
                  disabled={isLoading}
                >
                  {isLoading ? 'Booking...' : 'Book Now'}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
              <div className="relative">
                <img 
                  src={treatmentImage} 
                  alt="Gentle acupuncture and Chinese medicine support for miscarriage and pregnancy loss healing" 
                  className="rounded-2xl shadow-lg w-full h-auto"
                />
              </div>
            </div>
          </div>
        </section>
        {/* Why Support After Miscarriage Matters */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6 flex items-center justify-center gap-3">
                <Heart className="w-8 h-8 text-sage-600" />
                Why Support After Miscarriage Matters
              </h2>
            </div>
            <div className="bg-white rounded-2xl shadow-lg border-2 border-amber-200/50 p-8 md:p-12">
              <p className="text-lg text-sage-700 mb-6 leading-relaxed">
                In Chinese medicine, pregnancy loss can disturb the flow of Qi and Blood and leave the body and spirit in a state of imbalance. There may be lingering fatigue, bleeding, sadness, insomnia, hormonal disruption, or a deep sense of disconnection.
              </p>
              <p className="text-lg text-sage-700 leading-relaxed">
                Just as we prepare for birth and postpartum, we believe healing after loss also deserves care - rooted in nourishment, restoration, and emotional support. There's no timeline. There's no pressure. Just space to heal in your own time.
              </p>
            </div>
          </div>
        </section>
        {/* What We Offer */}
        <section className="py-16 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
                What We Offer
              </h2>
              <p className="text-xl text-sage-600 mb-8">
                Support for Miscarriage and Pregnancy Loss In-Clinic, At-Home, or Online
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Home className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">In-Clinic Care</h3>
                  </div>
                  <p className="text-sage-700 mb-4">
                    Our clinic is a quiet, compassionate space where you can safely land.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🪡</span>
                      <p className="text-sage-700">Acupuncture to support the uterus, regulate bleeding, calm the nervous system, and ground your emotions</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🌿</span>
                      <p className="text-sage-700">Chinese herbal medicine to clear stagnation, rebuild Blood, and support hormonal recalibration</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">💆‍♀️</span>
                      <p className="text-sage-700">Gentle bodywork to ease tension and reconnect with your body</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🕯️</span>
                      <p className="text-sage-700">Ritual-based support if desired - space to honour your grief or mark the transition with intention</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Globe className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">At-Home or Online Care</h3>
                  </div>
                  <p className="text-sage-700 mb-4 font-medium">
                    Support wherever you are in your journey.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <span className="text-lg">💬</span>
                      <p className="text-sage-700">Telehealth consults to talk through your experience, symptoms, and needs</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🌿</span>
                      <p className="text-sage-700">Herbal prescriptions tailored to the phase you're in - whether it's immediate support or longer-term healing</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🤍</span>
                      <p className="text-sage-700">Emotional care and guidance, without pressure or agenda</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
        {/* Recovery Timeline */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
                Recovery Timeline
              </h2>
            </div>
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="item-1" className="bg-white rounded-xl border-2 border-amber-200/50 shadow-lg mb-4">
                <AccordionTrigger className="px-6 py-4 text-left">
                  <div className="flex items-center gap-3">
                    <Flower2 className="w-6 h-6 text-sage-600" />
                    <span className="font-serif text-xl font-semibold text-sage-800">Immediate Post-Loss (0–4 weeks)</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-4">
                    <p className="text-sage-700 italic">👉 Clear remaining tissue or stagnation, regulate bleeding, support rest and hormone reset</p>
                  </div>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-2" className="bg-white rounded-xl border-2 border-amber-200/50 shadow-lg mb-4">
                <AccordionTrigger className="px-6 py-4 text-left">
                  <div className="flex items-center gap-3">
                    <Heart className="w-6 h-6 text-sage-600" />
                    <span className="font-serif text-xl font-semibold text-sage-800">Short-Term Recovery (1–3 months)</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-4">
                    <p className="text-sage-700 italic">👉 Nourish Blood and Qi, restore energy, regulate cycles, soothe the Heart</p>
                  </div>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-3" className="bg-white rounded-xl border-2 border-amber-200/50 shadow-lg">
                <AccordionTrigger className="px-6 py-4 text-left">
                  <div className="flex items-center gap-3">
                    <Sparkles className="w-6 h-6 text-sage-600" />
                    <span className="font-serif text-xl font-semibold text-sage-800">Long-Term Support (3+ months)</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-4">
                    <p className="text-sage-700 italic">👉 Rebuild Jing (essence), support fertility, integrate grief, regain confidence in your body</p>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </section>
        {/* What We Commonly Support */}
        <section className="py-16 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
                What We Commonly Support
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { western: "Prolonged bleeding or irregular cycles after miscarriage", chinese: "Qi deficiency, Blood stasis" },
                { western: "Fatigue, brain fog, and poor sleep", chinese: "Spleen Qi deficiency, Heart Blood deficiency" },
                { western: "Anxiety, sadness, emotional flatness", chinese: "Heart-Spleen disharmony, Liver Qi stagnation" },
                { western: "Hormonal imbalances", chinese: "Kidney deficiency, Yin/Yang disharmony" },
                { western: "Uterine cramping or clotting", chinese: "Cold in the uterus, Blood stasis" },
                { western: "Fear or disconnection from the body", chinese: "Shen disturbance, Jing depletion" }
              ].map((condition, index) => (
                <Card key={index} className="bg-white border-2 border-amber-200/50 shadow-sm rounded-xl hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-sage-800 mb-2">{condition.western}</h3>
                    <p className="text-sage-600 text-sm italic">{condition.chinese}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
        {/* What Makes Our Care Different */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                What Makes Our Care Different
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                { icon: "🫶", title: "We see the whole picture", desc: "Your grief, your hormones, your body, your spirit - they're all connected and worthy of care" },
                { icon: "🌿", title: "No rush, no shame", desc: "We meet you where you are. Whether it's been days or years, it's not too early or too late" },
                { icon: "📚", title: "Tools for healing, not just coping", desc: "From herbs to rituals to acupressure - we support your healing in practical, embodied ways" },
                { icon: "🕯️", title: "Sacred space for loss", desc: "We offer support with reverence and softness, not just protocols" }
              ].map((feature, index) => (
                <Card key={index} className="bg-white border-2 border-amber-200/50 shadow-sm rounded-xl hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 text-center">
                    <div className="text-3xl mb-3">{feature.icon}</div>
                    <h3 className="font-serif text-xl font-semibold text-sage-800 mb-2">{feature.title}</h3>
                    <p className="text-sage-700">{feature.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
        {/* Support Tools */}
        <section className="py-16 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                Support Tools We Offer
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: Leaf, title: "Custom Chinese herbal formulas", desc: "For post-loss recovery" },
                { icon: Coffee, title: "Warming teas", desc: "And Blood-building recipes" },
                { icon: Target, title: "Castor oil packs", desc: "And uterine care guidance" },
                { icon: Heart, title: "Grief-support practices", desc: "And ritual prompts" },
                { icon: BookOpen, title: "Recommended reading", desc: "The Little Book of Loss & Grief, Spirit Babies, The First Forty Days, Healing After Loss" }
              ].map((tool, index) => (
                <Card key={index} className="bg-white border-2 border-amber-200/50 shadow-sm rounded-xl hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 text-center">
                    <tool.icon className="w-8 h-8 text-sage-600 mx-auto mb-3" />
                    <h3 className="font-serif text-lg font-semibold text-sage-800 mb-2">{tool.title}</h3>
                    <p className="text-sage-700 text-sm">{tool.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
        {/* CTA Section */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
              Book Your Session
            </h2>
            <p className="text-xl text-sage-700 mb-8">
              You don't have to carry this alone. Let us hold space for your body, your grief, your next steps - whatever they may be.
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="flex items-center justify-center gap-3 text-sage-700">
                <MapPin className="w-5 h-5 text-sage-600" />
                <a 
                  href="https://maps.google.com/?q=700+Nicklin+Way+Currimundi+QLD" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-sage-800 transition-colors"
                >
                  700 Nicklin Way, Currimundi
                </a>
              </div>
              <div className="flex items-center justify-center gap-3 text-sage-700">
                <Phone className="w-5 h-5 text-sage-600" />
                <a 
                  href="tel:0415537784" 
                  className="hover:text-sage-800 transition-colors"
                >
                  0415 537 784
                </a>
              </div>
              <div className="flex items-center justify-center gap-3 text-sage-700">
                <Mail className="w-5 h-5 text-sage-600" />
                <a 
                  href="mailto:womenandtheway@gmail.com" 
                  className="hover:text-sage-800 transition-colors"
                >
                  womenandtheway@gmail.com
                </a>
              </div>
            </div>
            <Button 
              size="lg" 
              className="w-full sm:w-auto text-center break-words bg-sage-600 hover:bg-sage-700 text-white font-medium text-base px-8 py-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 mx-auto"
              onClick={handleBooking}
              disabled={isLoading}
            >
              {isLoading ? 'Booking...' : 'Your healing matters. And you are not alone.'}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </section>
        <ReturnToTop />
      </div>
    </>
  );
};

export default MiscarriageSupportPage; 